/**
 * Mirrors browser Pixel events to the server-side Conversions API.
 *
 * Meta flagged our event coverage: top-of-funnel events fired from the browser
 * only, so ad-blocked / cookie-restricted visitors never reached Meta. Each call
 * here carries the SAME `metaEventId` as the Pixel event, so Meta deduplicates
 * the pair instead of double-counting it.
 *
 * CompleteRegistration and Purchase/Subscribe ride along with their own API
 * calls (the flows that observe them) and must NOT be sent through here.
 * `InitiateCheckout` is the exception: it fires on the pay-button click, long
 * before the checkout request that would carry its identity, so it is mirrored
 * here to attach the logged-in buyer's hashed email/name to the click. The
 * server forwards it to the CAPI only for an authenticated user (see
 * meta-tracking.controller). `AddPaymentInfo` is its qualified-action twin and
 * rides on exactly the same terms.
 */
import { apiRequest } from './api';
import { getMetaContext } from './meta-cookies';
import { hasConsent } from './cookie-consent';
import type { PaymentType } from './payment-type';

/** Event names the public bridge endpoint accepts (mirrors the API allowlist). */
type TrackableEventName = 'ViewContent' | 'Search' | 'InitiateCheckout' | 'AddPaymentInfo';

/**
 * Fire-and-forget POST /meta/events. Analytics must never break a page or block
 * a render, so failures (offline, ad blocker nulling fetch, 4xx) are swallowed.
 */
export function sendMetaEvent(
  eventName: TrackableEventName,
  metaEventId: string,
  contentName?: string,
  /** The query, for `Search`. Ignored by every other event. */
  searchString?: string,
  /**
   * `InitiateCheckout` / `AddPaymentInfo` only: true for the one-time («Оплатить
   * разово») sheet, so the server values the CAPI leg at the +10% one-time price
   * and matches the Pixel. The server derives the number from the plan; this
   * only picks WHICH.
   */
  oneTime?: boolean,
  /**
   * `InitiateCheckout` / `AddPaymentInfo` only: the `payment_type` the caller
   * just put on the Pixel twin of this event — the SAME value, never derived a
   * second time. The server forwards it untouched to the CAPI leg; a
   * deduplicated pair keeps one leg's custom_data, so the two must agree.
   */
  paymentType?: PaymentType,
): void {
  if (typeof window === 'undefined') return;
  // Cookie consent: the CAPI twin of a Pixel event is advertising too — not sent
  // without a «publicitarias» grant (the API refuses it as well). Always on UZ/KZ.
  if (!hasConsent('marketing')) return;
  void apiRequest('/meta/events', {
    method: 'POST',
    body: JSON.stringify({
      eventName,
      metaEventId,
      contentName,
      searchString,
      oneTime,
      paymentType,
      ...getMetaContext(),
    }),
  }).catch(() => {
    // Best-effort beacon — never surface analytics failures to the user.
  });
}
